import EditorActions from 'components/Common/EditorActions'
import React, { useEffect, useRef, useState } from "react"
import { Button, FormGroup, Label } from "reactstrap"
import Select from "react-select"
import { capitalize } from "lodash"
import GooglePlacesAutocomplete, { geocodeByPlaceId, getLatLng } from "react-google-places-autocomplete"
import { LeadPanels } from "./LeadProfile"
import { withFloatingSurface } from 'components/Common/selectSurface'

export default function LeadEditForm({ draft, onChange, settings = {}, errors, dirty, locked, onSave, onCancel }) {
  const [country, setCountry] = useState(draft.location?.country || "")
  const [lookupError, setLookupError] = useState("")
  const [lookupPending, setLookupPending] = useState(false)
  const lookupVersion = useRef(0)
  useEffect(() => () => { lookupVersion.current += 1 }, [])
  const countries = settings.eligibleCountries || []
  const countryCode = countries.find(item => item.name === country)?.key
  const sports = [...new Set([...(settings.activeSports || []), ...draft.sports])]
  const setField = (name, value) => onChange(current => ({ ...current, [name]: value }))

  const field = (name, label, type = "text", extra = {}) => <FormGroup>
    <Label for={`lead-${name}`}>{label}</Label>
    <input id={`lead-${name}`} name={name} type={type} className={`form-control${errors[name] ? " is-invalid" : ""}`}
      value={draft[name]} onChange={event => setField(name, event.target.value)} disabled={locked}
      aria-invalid={!!errors[name]} aria-describedby={errors[name] ? `lead-${name}-error` : undefined} {...extra} />
    {errors[name] && <div className="invalid-feedback" id={`lead-${name}-error`}>{errors[name]}</div>}
  </FormGroup>

  const changeCountry = event => {
    lookupVersion.current += 1
    setCountry(event.target.value)
    setField("location", null)
    setLookupPending(false)
    setLookupError("")
  }
  const selectLocation = async option => {
    const version = ++lookupVersion.current
    setField("location", null)
    setLookupError("")
    if (!option) { setLookupPending(false); return }
    setLookupPending(true)
    try {
      const [result] = await geocodeByPlaceId(option.value.place_id)
      const components = result?.address_components || []
      const placeCountry = components.find(item => item.types.includes("country"))
      if (placeCountry?.short_name?.toLowerCase() !== countryCode?.toLowerCase()) {
        throw new Error("Choose a location in the selected country.")
      }
      const coords = await getLatLng(result)
      if (version !== lookupVersion.current) return
      const city = components.find(item => item.types.includes("locality")) ||
        components.find(item => item.types.includes("postal_town"))
      setField("location", { name: option.label, placeId: option.value.place_id,
        city: city?.long_name || null, country, coords })
    } catch (error) {
      if (version === lookupVersion.current) setLookupError("Could not verify the location. Please choose a location in the selected country and try again.")
    } finally {
      if (version === lookupVersion.current) setLookupPending(false)
    }
  }

  return <form onSubmit={event => { event.preventDefault(); if (!lookupPending) onSave() }} noValidate aria-label="Edit club lead">
    <LeadPanels club={<>
      {field("clubName", "Club name")}
      <FormGroup><Label for="lead-sports">Sports</Label>
        <Select styles={withFloatingSurface()} inputId="lead-sports" instanceId="lead-sports" isMulti isDisabled={locked}
          value={draft.sports.map(value => ({ value, label: capitalize(value) }))}
          options={sports.map(value => ({ value, label: capitalize(value) }))}
          onChange={values => setField("sports", (values || []).map(item => item.value))}
          classNamePrefix="lead-select" placeholder="No sports specified" />
      </FormGroup>
      {field("noCourts", "Courts", "number", { min: 0, step: 1 })}
      <FormGroup><Label for="lead-country">Country</Label>
        <select id="lead-country" className="custom-select" value={country} onChange={changeCountry} disabled={locked}>
          <option value="">Select country</option>
          {country && !countryCode && <option value={country}>{country}</option>}
          {countries.map(item => <option key={item.key} value={item.name}>{item.name}</option>)}
        </select>
      </FormGroup>
      <FormGroup><Label for="lead-location">Location</Label>
        <GooglePlacesAutocomplete key={country} apiKey={process.env.REACT_APP_GOOGLE_MAPS_API}
          onLoadFailed={() => setLookupError("Location search is unavailable. The saved location can still be kept.")}
          autocompletionRequest={{ componentRestrictions: { country: countryCode ? [countryCode.toLowerCase()] : [] } }}
          minLengthAutocomplete={3}
          selectProps={{ styles: withFloatingSurface(),  inputId: "lead-location", instanceId: "lead-location", classNamePrefix: "lead-select",
            value: draft.location ? { label: draft.location.name || draft.location.city, value: draft.location } : null,
            onChange: selectLocation, isClearable: true, isDisabled: locked || !countryCode,
            placeholder: country ? "Search for a location" : "Select a country first",
            "aria-invalid": !!errors.location, "aria-describedby": "lead-location-help" }} />
        <div id="lead-location-help" className={lookupError || errors.location ? "text-danger mt-2" : "text-muted mt-2"}>
          {lookupError || errors.location || (lookupPending ? "Verifying location…" :
            draft.location ? `Current location: ${draft.location.name || draft.location.city}` : "Select a matching location before saving.")}
        </div>
      </FormGroup>
      <FormGroup><Label for="lead-status">Status</Label>
        <select id="lead-status" className="custom-select" value={draft.status} onChange={event => setField("status", event.target.value)}
          disabled={locked} aria-invalid={!!errors.status}>
          <option value="">Select status</option><option value="pending">Pending</option><option value="closed">Closed</option>
        </select>{errors.status && <div className="text-danger">{errors.status}</div>}
      </FormGroup>
    </>} contact={<>
      {field("firstName", "First name")}
      {field("lastName", "Last name")}
      {field("jobTitle", "Job title (optional)")}
      {field("email", "Email", "email")}
      {field("phone", "Phone", "tel")}
      <p className="text-muted">Keep the country prefix and any leading zeros.</p>
    </>} />
    <EditorActions dirty={dirty} pending={locked}>
      <Button color="secondary" outline type="button" onClick={onCancel} disabled={locked}>Cancel</Button>
      <Button color="primary" type="submit" disabled={!dirty || locked || lookupPending}>Save changes</Button>
    </EditorActions>
  </form>
}
